import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Shield } from "lucide-react";
import logo from "@/assets/whatai-logo.png";

const VerifyPhone = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [code, setCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isVerified, setIsVerified] = useState(false);
  const [initialSmsSent, setInitialSmsSent] = useState(false);

  useEffect(() => {
    console.log("[VerifyPhone] Component mounted, user:", user);
    if (!user) {
      console.log("[VerifyPhone] No user, redirecting to auth");
      navigate("/auth");
      return;
    }

    checkVerificationStatus();
  }, [user, navigate]);

  const checkVerificationStatus = async () => {
    if (!user) return;

    console.log("[VerifyPhone] Checking verification status for user:", user.id);
    console.log("[VerifyPhone] User metadata:", user.user_metadata);

    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("phone, phone_verified")
        .eq("id", user.id)
        .single();

      console.log("[VerifyPhone] Profile data:", data);
      console.log("[VerifyPhone] Profile error:", error);

      if (error) throw error;

      if (data) {
        const phone = data.phone || user.user_metadata?.phone || "";
        console.log("[VerifyPhone] Phone number:", phone);
        setPhoneNumber(phone);
        
        if (data.phone_verified) {
          console.log("[VerifyPhone] Phone already verified, redirecting");
          setIsVerified(true);
          toast.success(t("toast.alreadyVerified"));
          setTimeout(() => navigate("/upload"), 2000);
        } else {
          console.log("[VerifyPhone] Phone not verified, auto-sending verification code");
          // Automatically send SMS when form appears
          if (!initialSmsSent) {
            setInitialSmsSent(true);
            handleSendCode();
          }
        }
      }
    } catch (error) {
      console.error("[VerifyPhone] Error checking verification status:", error);
    }
  };

  const handleSendCode = async () => {
    if (!user) return;

    console.log("[VerifyPhone] Sending verification code for user:", user.id);
    console.log("[VerifyPhone] Phone number:", phoneNumber);

    setIsSending(true);
    try {
      const { data, error } = await supabase.functions.invoke("send-verification-sms", {
        body: { userId: user.id },
      });

      console.log("[VerifyPhone] Send SMS response:", data);
      console.log("[VerifyPhone] Send SMS error:", error);

      if (error) throw error;

      if (data.success) {
        toast.success(t("toast.verificationSent"));
      } else {
        throw new Error(data.error || "Failed to send code");
      }
    } catch (error: any) {
      console.error("[VerifyPhone] Error sending code:", error);
      toast.error(error.message || t("toast.verificationFailed"));
    } finally {
      setIsSending(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !code.trim()) return;

    console.log("[VerifyPhone] Verifying code:", code.trim());
    console.log("[VerifyPhone] User ID:", user.id);

    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("verify-phone", {
        body: { 
          userId: user.id,
          code: code.trim()
        },
      });

      console.log("[VerifyPhone] Verify response:", data);
      console.log("[VerifyPhone] Verify error:", error);

      if (error) throw error;

      if (data.success) {
        console.log("[VerifyPhone] Verification successful!");
        toast.success(t("toast.verificationSuccess"));
        setIsVerified(true);
        setTimeout(() => navigate("/upload"), 2000);
      } else {
        throw new Error(data.error || "Verification failed");
      }
    } catch (error: any) {
      console.error("[VerifyPhone] Error verifying code:", error);
      toast.error(error.message || t("toast.invalidCode"));
    } finally {
      setIsLoading(false);
    }
  };

  if (isVerified) {
    return (
      <div className="min-h-screen bg-gradient-subtle flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-8 text-center">
          <div className="mb-6">
            <Shield className="w-16 h-16 mx-auto text-green-500 mb-4" />
            <h2 className="text-2xl font-bold text-primary mb-2">{t("verify.verified")}</h2>
            <p className="text-muted-foreground">
              {t("verify.redirecting")}
            </p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-subtle flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img 
            src={logo} 
            alt="WhatAI Logo" 
            className="w-20 h-20 mx-auto mb-4"
          />
          <h1 className="text-3xl font-bold text-primary mb-2">
            {t("verify.title")}
          </h1>
          <p className="text-muted-foreground">
            {t("verify.subtitle")} {phoneNumber}
          </p>
        </div>

        <Card className="p-6 shadow-elegant">
          <form onSubmit={handleVerify} className="space-y-6">
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 text-center">
              <p className="text-sm font-medium text-primary mb-1">
                {t("verify.reward")}
              </p>
              <p className="text-xs text-muted-foreground">
                {t("verify.rewardDesc")}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="code">{t("verify.codeLabel")}</Label>
              <Input
                id="code"
                type="text"
                placeholder={t("verify.codePlaceholder")}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                maxLength={6}
                required
                disabled={isLoading}
                className="text-center text-2xl tracking-widest"
              />
            </div>

            <div className="space-y-3">
              <Button
                type="submit"
                className="w-full gradient-primary text-white py-5 font-semibold"
                disabled={isLoading || code.length !== 6}
              >
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isLoading ? t("verify.verifying") : t("verify.verifyButton")}
              </Button>

              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={handleSendCode}
                disabled={isSending}
              >
                {isSending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isSending ? t("verify.sending") : t("verify.resendButton")}
              </Button>
            </div>

            <p className="text-xs text-center text-muted-foreground">
              {t("verify.expiry")}
            </p>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default VerifyPhone;
